import { useCallback, useMemo } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import { getLocalStorageItem } from "@utils/index";
import { USER_SESSION_KEY } from "@hooks/use-auth-context/utils";

const WP_USERS_CONFIG_URL = "wp-json/smassets/v1/site-users/config";

export type UserFieldConfig = {
    id: string;
    name: string;
    type: string;
    options: { value: string; label: string }[];
    clone: boolean;
    display: boolean;
    edit: boolean;
    filter: boolean;
    group: boolean;
    export: boolean;
    order?: number;
    required?: boolean;
    unique?: boolean;
};

export const useUsersConfig = () => {
    const { getApiUrl } = useMultiSiteContext();
    const configUrl = useMemo(() => getApiUrl(WP_USERS_CONFIG_URL), [getApiUrl]);

    const getConfig = useCallback(async (): Promise<Record<string, UserFieldConfig> | null> => {
        try {
            const token = getLocalStorageItem(USER_SESSION_KEY)?.authToken ?? "";
            const response = await fetchWithTokenRefresh(
                configUrl,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                },
                null
            );

            if (!response.ok) {
                return null;
            }

            const fields = (await response.json()) as Record<string, UserFieldConfig>;
            return Object.keys(fields).length > 0 ? fields : null;
        } catch {
            return null;
        }
    }, [configUrl]);

    return { getConfig };
};
